import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { UserRights, UserTypes } from "src/app/shared/types/user-info";
import { PaginatedResponse } from "../../../../core/http/paginated-response.type";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { DropdownOption, DropdownQuestion } from "../../../../shared/components/dynamic-form/question-dropdown";
import { TextboxQuestion } from "../../../../shared/components/dynamic-form/question-textbox";
import { setFilterCondition } from "../../../../shared/utils/helpers/common.helper";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { PartnerService } from "../../partners/partner.service";
import { Partner } from "../../partners/partner.type";
import { User } from "../user.type";

@Injectable()
export class ActivateUserService {
  private userType: string;
  constructor(private companyService: CompanyService, private partnerService: PartnerService) {}

  setUserType(userType: string) {
    this.userType = userType;
  }

  getPartners(query?: { name: string }): Observable<DropdownOption[]> {
    return this.partnerService
      .getPartners({ where: setFilterCondition(query, "$cont") })
      .pipe(map((r: PaginatedResponse<Partner>) => r.items.map((c: Partner) => ({ label: c.name, value: c.id }))));
  }

  getCompanies(query?: { partnerId: string; name?: string }): Observable<DropdownOption[]> {
    return this.companyService
      .getCompanies({ where: setFilterCondition(query, "$cont") })
      .pipe(map((r: PaginatedResponse<Company>) => r.items.map((c: Company) => ({ label: c.name, value: c.id }))));
  }

  createPartnerDropdown(options?: { hide: boolean }): DropdownQuestion {
    const dropdown = new DropdownQuestion({
      key: "partnerId",
      label: "Partner",
      required: true,
      hide: options?.hide,
      order: 7,
      dependsOn: {
        key: "type",
        callback: () => !options?.hide && this.getPartners(),
      },
      search: {
        filter: {
          callback: ({ value: name }) => !options?.hide && this.getPartners({ name }),
        },
      },
    });

    return dropdown;
  }

  createCompanyDropdown(options?: { hide: boolean }): DropdownQuestion {
    return new DropdownQuestion({
      key: "companyId",
      label: "Company",
      required: !options?.hide,
      hide: options?.hide,
      order: 8,
      dependsOn: {
        key: "partnerId",
        callback: ({ parentValue: partnerId }) => !options?.hide && this.getCompanies({ partnerId }),
      },
      search: {
        filter: {
          parentKey: "partnerId",
          callback: ({ parentValue: partnerId, value: name }) =>
            !options?.hide && this.getCompanies({ partnerId, name }),
        },
      },
    });
  }

  generateQuestions(options?: User): Observable<QuestionBase<string | boolean>[]> {
    const questions: QuestionBase<string | boolean>[] = [
      new TextboxQuestion({
        value: options.firstName,
        key: "firstName",
        label: "First name",
        required: true,
        disabled: true,
        order: 1,
      }),
      new TextboxQuestion({
        value: options.lastName,
        key: "lastName",
        label: "Last name",
        required: true,
        disabled: true,
        order: 2,
      }),
      new TextboxQuestion({
        value: options.email,
        key: "email",
        label: "Email",
        required: true,
        disabled: true,
        order: 3,
      }),
      new TextboxQuestion({
        value: options.phone || "",
        key: "phone",
        label: "Phone",
        required: false,
        disabled: true,
        order: 4,
      }),
      new DropdownQuestion({
        value: options?.type,
        key: "type",
        label: "User Type",
        required: true,
        order: 5,
        options: this.getUserTypeOptions(),
      }),

      new DropdownQuestion({
        value: options?.right,
        key: "right",
        label: "User Right",
        options: this.getUserRightOptions(),
        required: true,
        order: 6,
        dependsOn: {
          key: "type",
        },
      }),
      this.createPartnerDropdown({ hide: true }),
      this.createCompanyDropdown({ hide: true }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }
  /**
   * Get the dropdown options to populate on the user right dropdown
   * @returns rights list
   */
  private getUserRightOptions(): DropdownOption[] {
    return [
      { label: "Admin", value: UserRights.Admin },
      { label: "Manager", value: UserRights.Manager },
      { label: "Viewer", value: UserRights.Viewer },
    ];
  }

  /**
   * Get the dropdown options to populate on the user type dropdown
   * @returns types list
   */
  private getUserTypeOptions(): DropdownOption[] {
    const options: DropdownOption[] = [
      { label: "Global", value: UserTypes.Global },
      { label: "Partner", value: UserTypes.Partner },
      { label: "Company", value: UserTypes.Company },
    ];

    return options;
  }
}
